import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  CalendarCheck,
  MessageCircle,
  Mail,
  FileDown,
  CheckSquare,
  Sparkles,
  Building2,
  Info,
} from "lucide-react";
import type { QualificationResult, WizardAnswers, Lang, ResultCase } from "@/lib/types";
import { generateChecklistPdf, downloadPdf } from "@/lib/checklist-pdf";

interface ResultLayoutProps {
  result: QualificationResult;
  answers: WizardAnswers;
  lang: Lang;
  badgeLabelKey: string;
  titleKey: string;
  subtitleKey: string;
  badgeVariant: ResultCase;
  showTicket?: boolean;
  showDocuments?: boolean;
  primaryCta?: { label: string; href?: string; onClick?: () => void };
  secondaryMessage?: React.ReactNode;
  extraTopBlock?: React.ReactNode;
}

const BADGE_CLASS: Record<ResultCase, string> = {
  A: "badge-success",
  B: "badge-warning",
  C: "badge-danger",
  D: "badge-info",
};

const DOC_LABELS_FR: Record<string, string> = {
  passeport: "Passeport en cours de validité",
  visa_long_sejour: "Visa long séjour",
  certificat_scolarite: "Certificat de scolarité",
  attestation_logement: "Attestation de logement",
  ofii_attestation: "Attestation OFII",
  contrat_travail: "Contrat de travail",
  "3_bulletins_salaire": "3 derniers bulletins de salaire",
  attestation_employeur: "Attestation employeur",
  justif_domicile_3_mois: "Justificatif de domicile (- de 3 mois)",
  titre_actuel: "Titre de séjour actuel",
  fiches_paie_6_mois_ou_avis_imposition: "Fiches de paie 6 mois OU avis d'imposition",
  photo_identite: "Photo d'identité aux normes",
  timbre_fiscal: "Timbre fiscal",
  titre_expire: "Titre de séjour expiré",
  preuve_continuite_residence: "Preuves de continuité de résidence",
  titre_de_sejour: "Titre de séjour",
  acte_naissance_traduit: "Acte de naissance traduit (assermenté)",
  casier_judiciaire_pays_origine: "Casier judiciaire du pays d'origine",
  casier_judiciaire_france: "Casier judiciaire français",
  fiches_paie_3_ans_ou_bilans: "Fiches de paie 3 ans OU bilans",
  avis_imposition_3_ans: "Avis d'imposition 3 dernières années",
  justif_domicile: "Justificatif de domicile",
  attestation_b2_francais: "Attestation niveau B2 français",
  titre_vie_privee_familiale: "Titre vie privée et familiale",
  livret_famille_traduit: "Livret de famille traduit",
  acte_mariage_transcrit: "Acte de mariage transcrit",
  preuve_communaute_de_vie_4_ans: "Preuves de communauté de vie 4 ans",
  justif_domicile_avec_surface_logement: "Justificatif de domicile avec surface logement",
  "12_fiches_paie_ou_avis_imposition_12_mois": "12 fiches de paie OU avis d'imposition 12 mois",
  ofii_attestation_parcours_integration: "Attestation OFII parcours d'intégration",
  ancienne_cnie_ou_passeport: "Ancienne CNIE ou passeport marocain",
  photos_format_marocain: "Photos format consulat marocain",
  acte_naissance_marocain_recent: "Acte de naissance marocain récent",
  acte_a_transcrire_original: "Acte à transcrire (original)",
  traduction_assermentee: "Traduction assermentée",
  passeport_marocain: "Passeport marocain",
  livret_famille_si_applicable: "Livret de famille (si applicable)",
  decision_administrative_recente: "Décision administrative récente (refus, OQTF, etc.)",
  tout_courrier_prefecture: "Tout courrier de la préfecture",
  passeport_ou_acte_naissance: "Passeport OU acte de naissance",
  preuves_presence_france_3_ans: "Preuves de présence en France sur 3 ans",
  fiches_paie_meme_dissimulees: "Fiches de paie (même travail dissimulé)",
  tout_justif_domicile: "Tout justificatif de domicile possible",
  tout_courrier_administratif_recent: "Tout courrier administratif récent",
  titre_actuel_si_existe: "Titre actuel (si existe)",
  piece_identite: "Pièce d'identité",
  courrier_concernant_demarche: "Courrier concernant la démarche",
};

export function ResultLayout(props: ResultLayoutProps) {
  const { t } = useTranslation();
  const { result, answers, lang } = props;
  const [recapOpen, setRecapOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const ticketDisplay = useMemo(() => {
    if (!props.showTicket || !result.ticket_visible) return null;
    if (result.ticket_estimate === null || result.ticket_estimate === 0) return null;
    if (result.ticket_post_audit_min && result.ticket_post_audit_max) {
      return `${result.ticket_estimate} € (audit) → ${result.ticket_post_audit_min}-${result.ticket_post_audit_max} € (dossier)`;
    }
    if (result.ticket_max) {
      return `${result.ticket_estimate} - ${result.ticket_max} €`;
    }
    return `${result.ticket_estimate} €`;
  }, [props.showTicket, result]);

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const bytes = await generateChecklistPdf({ answers, result, lang });
      downloadPdf(bytes, `levelcare-checklist-${answers.contact.prenom.toLowerCase()}.pdf`);
    } finally {
      setPdfLoading(false);
    }
  };

  const waMessage = encodeURIComponent(
    `Bonjour, je viens de faire le simulateur LevelCare. Mon résultat: ${result.service}. Mon prénom: ${answers.contact.prenom}.`,
  );
  const waHref = `https://wa.me/33619943917?text=${waMessage}`;

  return (
    <div
      className="mx-auto max-w-wizard px-4 py-8 md:px-0 md:py-12"
      role="region"
      aria-live="polite"
    >
      <div className="card-result">
        <div className="flex items-center gap-3">
          <span className={BADGE_CLASS[props.badgeVariant]}>{t(props.badgeLabelKey)}</span>
        </div>
        <h1 className="mt-4 text-balance">{t(props.titleKey)}</h1>
        <p className="mt-3 text-body-long text-ink-soft">{t(props.subtitleKey)}</p>

        {result.ai_contradiction && (
          <div className="mt-5 flex items-start gap-2 rounded-input border border-warning/30 bg-warning/5 p-3 text-caption text-warning">
            <Info size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
            <span>
              On a détecté une situation qui mérite un échange direct avec un conseiller. Le RDV est
              priorisé.
            </span>
          </div>
        )}

        {props.extraTopBlock}

        <div className="mt-7 grid gap-5 md:grid-cols-2">
          <Block label={t("result.service_label")} icon={<Sparkles size={18} className="text-signal" />}>
            <p className="font-display text-body font-semibold text-ink">{result.service}</p>
          </Block>

          {ticketDisplay && (
            <Block label={t("result.ticket_label")}>
              <p className="font-display text-body font-semibold text-ink">{ticketDisplay}</p>
              {result.ticket_note_key === "ticket.consular_fees_separate" && (
                <p className="mt-1 text-caption text-muted">
                  {t("result.ticket_consular_fees_separate")}
                </p>
              )}
            </Block>
          )}

          {result.prefecture && (
            <Block
              label={t("result.prefecture_label")}
              icon={<Building2 size={18} className="text-signal" />}
            >
              <p className="font-body text-body text-ink">{result.prefecture.name}</p>
              {result.prefecture.address && (
                <p className="text-caption text-muted">{result.prefecture.address}</p>
              )}
            </Block>
          )}
        </div>

        {result.mandatory_mention_key === "naturalisation_2026_b2" && (
          <p className="mt-5 rounded-input bg-cream p-3 text-caption text-ink-soft">
            {t("result.naturalisation_2026_b2")}
          </p>
        )}

        {props.secondaryMessage}

        {props.showDocuments && result.documents_required.length > 0 && (
          <div className="mt-8">
            <h3 className="font-display text-h3 text-ink">{t("result.documents_label")}</h3>
            <ul className="mt-4 flex flex-col gap-2">
              {result.documents_required.map((docId) => (
                <li
                  key={docId}
                  className="flex items-start gap-3 rounded-input bg-cream p-3 text-body text-ink"
                >
                  <CheckSquare size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-signal" />
                  <span>{DOC_LABELS_FR[docId] ?? docId}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.upsell_mre && (
          <div className="upsell-card-mre mt-8">
            <p>{t("result.upsell.mre")}</p>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
          {props.primaryCta && (
            props.primaryCta.href ? (
              <a
                href={props.primaryCta.href}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full justify-center md:w-auto"
              >
                <CalendarCheck size={20} strokeWidth={1.75} />
                {props.primaryCta.label}
              </a>
            ) : (
              <button
                type="button"
                onClick={props.primaryCta.onClick}
                className="btn-primary w-full justify-center md:w-auto"
              >
                <CalendarCheck size={20} strokeWidth={1.75} />
                {props.primaryCta.label}
              </button>
            )
          )}
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary w-full justify-center md:w-auto"
          >
            <MessageCircle size={20} strokeWidth={1.75} />
            {t("result.cta_whatsapp")}
          </a>
          <a
            href={`mailto:${answers.contact.email}`}
            className="btn-secondary w-full justify-center md:w-auto"
          >
            <Mail size={20} strokeWidth={1.75} />
            {t("result.cta_email")}
          </a>
        </div>

        {props.showDocuments && result.documents_required.length > 0 && (
          <button
            type="button"
            onClick={handlePdf}
            disabled={pdfLoading}
            className="mt-3 inline-flex items-center gap-2 text-caption text-muted hover:text-ink"
          >
            <FileDown size={16} strokeWidth={1.75} />
            {pdfLoading ? "..." : t("result.cta_pdf")}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => setRecapOpen(!recapOpen)}
        className="mt-4 flex w-full items-center justify-between rounded-card bg-paper px-4 py-3 text-caption text-muted hover:bg-cream"
        aria-expanded={recapOpen}
      >
        <span>{t("result.summary_label")}</span>
        <ChevronDown
          size={16}
          strokeWidth={1.75}
          className={"transition-transform " + (recapOpen ? "rotate-180" : "")}
        />
      </button>
      {recapOpen && (
        <div className="mt-2 rounded-card bg-paper p-4 text-caption text-ink-soft">
          <Recap answers={answers} />
        </div>
      )}
    </div>
  );
}

function Block({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-caption text-muted">
        {icon}
        <span>{label}</span>
      </div>
      {children}
    </div>
  );
}

function Recap({ answers }: { answers: WizardAnswers }) {
  const { t } = useTranslation();
  const items: { label: string; value: string }[] = [
    { label: t("q.besoin._label"), value: t(`q.besoin.${answers.besoin}`) },
    { label: t("q.nationalite._label"), value: answers.nationalite },
    { label: t("q.situation._label"), value: t(`q.situation.${answers.situation}`) },
    { label: t("q.duree_france._label"), value: t(`q.duree_france.${answers.duree_france}`) },
    { label: t("q.profession._label"), value: t(`q.profession.${answers.profession}`) },
    {
      label: t("q.evenements_passes._label"),
      value: answers.evenements_passes.map((e) => t(`q.evenements_passes.${e}`)).join(", "),
    },
  ];
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((it) => (
        <li key={it.label} className="flex items-start justify-between gap-4">
          <span className="text-muted">{it.label}</span>
          <span className="text-right text-ink">{it.value}</span>
        </li>
      ))}
    </ul>
  );
}
